import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const inputStyle = {
  border: '2px solid rgba(59,130,246,0.3)', borderRadius: '8px',
  padding: '0.75rem', fontSize: '1rem', backgroundColor: '#fafafa',
  width: '100%', transition: 'all 0.2s',
};
const focus = (e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; };
const blur  = (e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; };

const Catalogos = () => {
  const { user } = useAuth();

  // Catálogos
  const [catalogos, setCatalogos] = useState([]);
  const [catalogoActivo, setCatalogoActivo] = useState(null);
  const [loadingCat, setLoadingCat] = useState(true);

  // Productos
  const [productos, setProductos] = useState([]);
  const [loadingProd, setLoadingProd] = useState(false);

  // Prospectos para envío WA
  const [prospectos, setProspectos] = useState([]);

  // Modales
  const [showModalCat, setShowModalCat] = useState(false);
  const [showModalProd, setShowModalProd] = useState(false);
  const [showModalEnviar, setShowModalEnviar] = useState(false);
  const [buscarContacto, setBuscarContacto] = useState('');
  const [showImagenModal, setShowImagenModal] = useState(null);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingProdId, setEditingProdId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [prospectoEnvio, setProspectoEnvio] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [formCat, setFormCat] = useState({ nombre: '', descripcion: '' });
  const [formProd, setFormProd] = useState({ nombre: '', descripcion: '', condiciones: '', precio: '', imagenes: [] });

  // Código del tenant para URL pública
  const codigoTenant = user?.tenant?.codigo || '';

  // ── API ───────────────────────────────────────────────────────
  const fetchCatalogos = async () => {
    setLoadingCat(true);
    try {
      const { data } = await api.get('/catalogos');
      setCatalogos(data);
      if (data.length && !catalogoActivo) setCatalogoActivo(data[0]);
    } catch { toast.error('Error al cargar catálogos'); }
    finally { setLoadingCat(false); }
  };

  const fetchProductos = async (catId) => {
    setLoadingProd(true);
    try {
      const { data } = await api.get(`/catalogos/${catId}/productos`);
      setProductos(data);
    } catch { toast.error('Error al cargar productos'); }
    finally { setLoadingProd(false); }
  };

  const fetchProspectos = async () => {
    try {
      const { data } = await api.get('/crm/prospectos?limit=100');
      setProspectos((data.prospectos || []).filter(p => p.whatsapp_number));
    } catch {}
  };

  useEffect(() => { fetchCatalogos(); fetchProspectos(); }, []);
  useEffect(() => { if (catalogoActivo) fetchProductos(catalogoActivo.id); }, [catalogoActivo]);

  // ── Catálogos ─────────────────────────────────────────────────
  const handleSubmitCat = async (e) => {
    e.preventDefault();
    try {
      if (editingCatId) {
        await api.put(`/catalogos/${editingCatId}`, formCat);
        toast.success('Catálogo actualizado');
      } else {
        await api.post('/catalogos', formCat);
        toast.success('Catálogo creado');
      }
      closeCatModal();
      fetchCatalogos();
    } catch { toast.error('Error al guardar catálogo'); }
  };

  const handleDeleteCat = (cat) => {
    setConfirmAction({
      mensaje: `¿Eliminar el catálogo "${cat.nombre}" y todos sus productos?`,
      onConfirm: async () => {
        try {
          await api.delete(`/catalogos/${cat.id}`);
          toast.success('Catálogo eliminado');
          setCatalogoActivo(null);
          setProductos([]);
          fetchCatalogos();
        } catch { toast.error('Error al eliminar'); }
      },
    });
  };

  const openEditCat = (cat) => {
    setFormCat({ nombre: cat.nombre, descripcion: cat.descripcion || '' });
    setEditingCatId(cat.id);
    setShowModalCat(true);
  };

  const closeCatModal = () => {
    setShowModalCat(false);
    setEditingCatId(null);
    setFormCat({ nombre: '', descripcion: '' });
  };

  // ── Productos ─────────────────────────────────────────────────
  const handleSubmitProd = async (e) => {
    e.preventDefault();
    try {
      // Procesar imágenes: subir base64 nuevas, mantener rutas existentes
      let imagenesFinal = [];
      for (const img of formProd.imagenes) {
        if (img.startsWith('data:')) {
          // Nueva imagen en base64 → subirla
          const { data } = await api.post('/catalogos/upload-imagen', { base64: img });
          imagenesFinal.push(data.path);
        } else {
          // Ruta de archivo existente → mantener
          imagenesFinal.push(img);
        }
      }

      const payload = {
        ...formProd,
        imagen: imagenesFinal.length > 0 ? JSON.stringify(imagenesFinal) : null,
      };

      if (editingProdId) {
        await api.put(`/catalogos/productos/${editingProdId}`, payload);
        toast.success('Producto actualizado');
      } else {
        await api.post(`/catalogos/${catalogoActivo.id}/productos`, payload);
        toast.success('Producto creado');
      }
      closeProdModal();
      fetchProductos(catalogoActivo.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar producto');
    }
  };

  const handleDeleteProd = (prod) => {
    setConfirmAction({
      mensaje: `¿Eliminar el producto "${prod.nombre}"?`,
      onConfirm: async () => {
        try {
          await api.delete(`/catalogos/productos/${prod.id}`);
          toast.success('Producto eliminado');
          fetchProductos(catalogoActivo.id);
        } catch { toast.error('Error al eliminar'); }
      },
    });
  };

  const openEditProd = async (prod) => {
    let imagenes = [];
    let imagen = prod.imagen;

    // La lista solo trae la primera imagen ($[0]) — cargar siempre el producto completo
    // para no perder las demás al guardar
    if (prod.id) {
      try {
        const { data } = await api.get(`/catalogos/productos/${prod.id}`);
        imagen = data.imagen;
      } catch {
        // Sin imagen, continuar
      }
    }

    // La columna es JSON: mysql2 la devuelve ya como array (o string en datos viejos)
    if (Array.isArray(imagen)) imagenes = imagen;
    else if (imagen) {
      try { imagenes = JSON.parse(imagen); if (!Array.isArray(imagenes)) imagenes = [imagenes]; }
      catch { imagenes = [imagen]; }
    }
    imagenes = imagenes.filter(img => typeof img === 'string' && img).slice(0, 3);
    setFormProd({
      nombre: prod.nombre,
      descripcion: prod.descripcion || '',
      condiciones: prod.condiciones || '',
      precio: prod.precio || '',
      imagenes,
    });
    setEditingProdId(prod.id);
    setShowModalProd(true);
  };

  const closeProdModal = () => {
    setShowModalProd(false);
    setEditingProdId(null);
    setFormProd({ nombre: '', descripcion: '', condiciones: '', precio: '', imagenes: [] });
  };

  // ── Imagen ────────────────────────────────────────────────────
  const handleImagenChange = (e) => {
    const files = Array.from(e.target.files);
    const actuales = formProd.imagenes.length;
    const disponibles = 3 - actuales;
    if (disponibles <= 0) { toast.error('Máximo 3 imágenes por producto'); return; }
    const seleccionados = files.slice(0, disponibles);
    seleccionados.forEach(file => {
      if (file.size > 3 * 1024 * 1024) { toast.error(`${file.name} supera 3MB`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => setFormProd(prev => ({
        ...prev,
        imagenes: [...prev.imagenes, ev.target.result].slice(0, 3),
      }));
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleEliminarImagen = (idx) => {
    setFormProd(prev => ({ ...prev, imagenes: prev.imagenes.filter((_, i) => i !== idx) }));
  };

  // ── Enviar WhatsApp ───────────────────────────────────────────
  const handleEnviarWA = async () => {
    if (!prospectoEnvio) { toast.error('Selecciona un contacto'); return; }
    setEnviando(true);
    try {
      const { data } = await api.post(`/catalogos/productos/${productoSeleccionado.id}/enviar-whatsapp`, {
        prospecto_id: prospectoEnvio,
      });
      toast.success(data.mensaje);
      setShowModalEnviar(false);
      setProspectoEnvio('');
      setBuscarContacto('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al enviar');
    } finally {
      setEnviando(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="page-header">
        <h2>🛍️ Catálogos</h2>
      </div>

      {/* Apartado link público — igual a reserva pública */}
      {codigoTenant && (
        <div className="card" style={{
          marginBottom: '1.5rem',
          padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          border: '1px solid #bfdbfe',
          display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#1d4ed8', marginBottom: '0.35rem' }}>
              🔗 Link público de tu catálogo
            </div>
            <div style={{
              fontSize: '0.875rem', color: '#1e40af', fontFamily: 'monospace',
              background: '#fff', padding: '0.4rem 0.75rem', borderRadius: '6px',
              border: '1px solid #bfdbfe', wordBreak: 'break-all',
            }}>
              {window.location.origin}/agenda/catalogo/{codigoTenant}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/agenda/catalogo/${codigoTenant}`);
                toast.success('¡Link copiado!');
              }}
              style={{ padding: '0.5rem 1rem', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
            >
              📋 Copiar
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Mira nuestro catálogo de productos: ${window.location.origin}/agenda/catalogo/${codigoTenant}`)}`}
              target="_blank"
              rel="noreferrer"
              style={{ padding: '0.5rem 1rem', background: '#25d366', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              💬 Compartir por WA
            </a>
            <a
              href={`${window.location.origin}/agenda/catalogo/${codigoTenant}`}
              target="_blank"
              rel="noreferrer"
              style={{ padding: '0.5rem 1rem', background: 'white', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              👁️ Ver catálogo
            </a>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '1.5rem', alignItems: 'start' }}>

        {/* Panel izquierdo — lista de catálogos */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#374151' }}>Mis catálogos</span>
              <span style={{
                marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: '600',
                color: catalogos.length >= 2 ? '#dc2626' : '#6b7280',
              }}>
                {catalogos.length}/2
              </span>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowModalCat(true)}
              disabled={catalogos.length >= 2}
              title={catalogos.length >= 2 ? 'Límite de 2 catálogos alcanzado' : ''}
              style={{ fontSize: '0.75rem', padding: '0.3rem 0.65rem', opacity: catalogos.length >= 2 ? 0.5 : 1 }}
            >
              + Nuevo
            </button>
          </div>
          {loadingCat ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}><div className="loading" /></div>
          ) : catalogos.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
              Sin catálogos aún
            </div>
          ) : (
            catalogos.map(cat => (
              <div
                key={cat.id}
                onClick={() => setCatalogoActivo(cat)}
                style={{
                  padding: '0.75rem 1rem', cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  borderLeft: `3px solid ${cat.id === catalogoActivo?.id ? '#2563eb' : 'transparent'}`,
                  background: cat.id === catalogoActivo?.id ? '#eff6ff' : 'transparent',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (cat.id !== catalogoActivo?.id) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => { if (cat.id !== catalogoActivo?.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '0.875rem', color: '#111827' }}>{cat.nombre}</div>
                    <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.1rem' }}>
                      {cat.total_productos} producto{cat.total_productos !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => openEditCat(cat)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#6b7280', padding: '0.2rem' }}>✏️</button>
                    <button onClick={() => handleDeleteCat(cat)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#ef4444', padding: '0.2rem' }}>🗑️</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Panel derecho — productos */}
        <div>
          {!catalogoActivo ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🛍️</div>
              <p>Selecciona o crea un catálogo para ver sus productos</p>
            </div>
          ) : (
            <>
              <div className="card" style={{ padding: '0.875rem 1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>{catalogoActivo.nombre}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.2rem' }}>
                    {catalogoActivo.descripcion && (
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>{catalogoActivo.descripcion}</p>
                    )}
                    <span style={{
                      fontSize: '0.72rem', fontWeight: '600',
                      color: productos.length >= 10 ? '#dc2626' : '#6b7280',
                    }}>
                      {productos.length}/10 productos
                    </span>
                    {productos.length >= 10 && (
                      <span style={{ fontSize: '0.72rem', color: '#dc2626', background: '#fee2e2', borderRadius: '4px', padding: '0.1rem 0.4rem', fontWeight: '600' }}>
                        Límite alcanzado
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowModalProd(true)}
                  disabled={productos.length >= 10}
                  title={productos.length >= 10 ? 'Límite de 10 productos alcanzado' : ''}
                  style={{ fontSize: '0.82rem', whiteSpace: 'nowrap', opacity: productos.length >= 10 ? 0.5 : 1 }}
                >
                  + Nuevo producto
                </button>
              </div>

              {loadingProd ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading" /></div>
              ) : productos.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📦</div>
                  <p>Este catálogo no tiene productos aún</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
                  {productos.map(prod => (
                    <div key={prod.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                      {/* Primera imagen como referencia */}
                      {prod.imagen ? (
                        <img src={prod.imagen} alt={prod.nombre}
                          onClick={() => setShowImagenModal(prod.imagen)}
                          style={{ width: '100%', height: '180px', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', height: '120px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d5db', fontSize: '2.5rem' }}>📦</div>
                      )}

                      <div style={{ padding: '0.875rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                          <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#111827' }}>{prod.nombre}</h4>
                          {prod.precio > 0 && (
                            <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '6px', padding: '0.15rem 0.5rem', fontSize: '0.78rem', fontWeight: '700', flexShrink: 0, marginLeft: '0.5rem' }}>
                              ${parseFloat(prod.precio).toFixed(2)}
                            </span>
                          )}
                        </div>

                        {prod.descripcion && (
                          <p style={{ margin: '0 0 0.4rem', fontSize: '0.8rem', color: '#6b7280', lineHeight: '1.4' }}>
                            {prod.descripcion}
                          </p>
                        )}
                        {prod.condiciones && (
                          <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
                            📋 {prod.condiciones}
                          </p>
                        )}

                        <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => { setProductoSeleccionado(prod); setShowModalEnviar(true); }}
                            style={{
                              flex: 1, background: '#d1fae5', color: '#065f46', border: 'none',
                              borderRadius: '6px', padding: '0.4rem 0.6rem', cursor: 'pointer',
                              fontSize: '0.75rem', fontWeight: '600',
                            }}
                          >
                            💬 Enviar por WA
                          </button>
                          <button onClick={() => openEditProd(prod)}
                            style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.4rem 0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: '#6b7280' }}>
                            ✏️
                          </button>
                          <button onClick={() => handleDeleteProd(prod)}
                            style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.4rem 0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: '#ef4444' }}>
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modal catálogo ── */}
      {showModalCat && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.5rem 1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>🛍️</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '700' }}>{editingCatId ? 'Editar catálogo' : 'Nuevo catálogo'}</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>Organiza tus productos por categoría</p>
                </div>
              </div>
              <button onClick={closeCatModal} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <form onSubmit={handleSubmitCat}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📋 Datos del catálogo</span>
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                    Nombre <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input type="text" className="form-input" value={formCat.nombre}
                    onChange={e => setFormCat({ ...formCat, nombre: e.target.value })}
                    required placeholder="Ej. Ropa de temporada" style={inputStyle}
                    onFocus={focus} onBlur={blur} />
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Descripción</label>
                  <textarea className="form-input" value={formCat.descripcion}
                    onChange={e => setFormCat({ ...formCat, descripcion: e.target.value })}
                    rows={3} placeholder="Descripción del catálogo..."
                    style={{ ...inputStyle, resize: 'vertical' }} onFocus={focus} onBlur={blur} />
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeCatModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingCatId ? 'Guardar cambios' : '+ Crear catálogo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal producto ── */}
      {showModalProd && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '580px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.5rem 1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>📦</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '700' }}>{editingProdId ? 'Editar producto' : 'Nuevo producto'}</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>{catalogoActivo?.nombre}</p>
                </div>
              </div>
              <button onClick={closeProdModal} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <form onSubmit={handleSubmitProd}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Datos del producto */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📋 Información del producto</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Nombre <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input type="text" className="form-input" value={formProd.nombre}
                        onChange={e => setFormProd({ ...formProd, nombre: e.target.value })}
                        required placeholder="Nombre del producto" style={inputStyle}
                        onFocus={focus} onBlur={blur} />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Descripción</label>
                      <textarea className="form-input" value={formProd.descripcion}
                        onChange={e => setFormProd({ ...formProd, descripcion: e.target.value })}
                        rows={3} placeholder="Describe el producto..."
                        style={{ ...inputStyle, resize: 'vertical' }} onFocus={focus} onBlur={blur} />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Condiciones</label>
                      <textarea className="form-input" value={formProd.condiciones}
                        onChange={e => setFormProd({ ...formProd, condiciones: e.target.value })}
                        rows={2} placeholder="Ej: Disponible solo en tienda, aplican restricciones..."
                        style={{ ...inputStyle, resize: 'vertical' }} onFocus={focus} onBlur={blur} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Precio</label>
                      <input type="number" step="0.01" min="0" className="form-input" value={formProd.precio}
                        onChange={e => setFormProd({ ...formProd, precio: e.target.value })}
                        placeholder="0.00" style={inputStyle} onFocus={focus} onBlur={blur} />
                    </div>
                  </div>
                </div>

                {/* Imagen */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>🖼️ Imagen del producto</span>
                  </div>
                  {/* Previews de imágenes */}
                  {formProd.imagenes.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      {formProd.imagenes.map((img, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img src={img} alt={`img-${idx}`}
                            style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                          <button
                            type="button"
                            onClick={() => handleEliminarImagen(idx)}
                            style={{
                              position: 'absolute', top: '-6px', right: '-6px',
                              background: '#ef4444', color: 'white', border: 'none',
                              borderRadius: '50%', width: '20px', height: '20px',
                              cursor: 'pointer', fontSize: '0.7rem', fontWeight: '700',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Botón agregar imagen */}
                  {formProd.imagenes.length < 3 && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                        color: 'white', borderRadius: '8px',
                        padding: '0.55rem 1.1rem', fontSize: '0.875rem',
                        fontWeight: '600', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                        boxShadow: '0 2px 6px rgba(37,99,235,0.3)',
                      }}>
                        🖼️ Seleccionar imagen
                      </div>
                      <input type="file" accept="image/*" multiple onChange={handleImagenChange} style={{ display: 'none' }} />
                    </label>
                  )}
                  <small style={{ display: 'block', marginTop: '0.4rem', color: '#9ca3af' }}>
                    {formProd.imagenes.length}/3 imágenes · Máximo 3MB por imagen. JPG, PNG o WebP.
                  </small>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeProdModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingProdId ? 'Guardar cambios' : '+ Crear producto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal enviar WA ── */}
      {showModalEnviar && productoSeleccionado && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>💬</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: '700' }}>Enviar por WhatsApp</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem' }}>{productoSeleccionado.nombre}</p>
                </div>
              </div>
              <button onClick={() => { setShowModalEnviar(false); setProspectoEnvio(''); }}
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Selecciona el contacto</span>
              </div>

              {/* Buscador */}
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  placeholder="🔍 Buscar por nombre o número..."
                  value={buscarContacto}
                  onChange={e => setBuscarContacto(e.target.value)}
                  autoFocus
                  style={{ ...inputStyle, fontSize: '0.875rem' }}
                  onFocus={focus} onBlur={blur}
                />
              </div>

              {/* Lista de contactos */}
              {prospectos.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                  No hay contactos con número WhatsApp en el CRM
                </p>
              ) : (() => {
                const filtrados = prospectos.filter(p => {
                  const q = buscarContacto.toLowerCase();
                  return !q ||
                    `${p.nombres} ${p.apellidos || ''}`.toLowerCase().includes(q) ||
                    (p.whatsapp_number || '').includes(q);
                });
                return filtrados.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                    Sin resultados para "{buscarContacto}"
                  </p>
                ) : (
                  <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {filtrados.map((p, idx) => {
                      const nombre = `${p.nombres}${p.apellidos ? ' ' + p.apellidos : ''}`;
                      const isSelected = prospectoEnvio === String(p.id);
                      return (
                        <div
                          key={p.id}
                          onClick={() => setProspectoEnvio(String(p.id))}
                          style={{
                            padding: '0.65rem 0.875rem',
                            borderBottom: idx < filtrados.length - 1 ? '1px solid #f3f4f6' : 'none',
                            cursor: 'pointer',
                            background: isSelected ? '#eff6ff' : 'white',
                            borderLeft: `3px solid ${isSelected ? '#2563eb' : 'transparent'}`,
                            transition: 'background 0.1s',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
                        >
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#111827' }}>{nombre}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.1rem' }}>📱 {p.whatsapp_number}</div>
                          </div>
                          {isSelected && <span style={{ color: '#2563eb', fontSize: '1rem' }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.5rem' }}>
              <button className="btn btn-secondary" onClick={() => { setShowModalEnviar(false); setProspectoEnvio(''); setBuscarContacto(''); }}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleEnviarWA} disabled={enviando || !prospectoEnvio} style={{ minWidth: '120px' }}>
                {enviando ? '⏳ Enviando...' : '💬 Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal imagen grande ── */}
      {showImagenModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div onClick={() => setShowImagenModal(null)} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
            <img src={showImagenModal} alt="producto"
              style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '12px', objectFit: 'contain', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} />
          </div>
        </div>
      )}

      {/* ── Modal confirmar ── */}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>⚠️</div>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: '700' }}>Confirmar acción</h3>
              </div>
              <button onClick={() => setConfirmAction(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div className="modal-body"><p style={{ color: '#374151' }}>{confirmAction.mensaje}</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Catalogos;
